import { useRef, useEffect, useState } from "react";
import FormatBoldIcon from '@mui/icons-material/FormatBold';
import FormatItalicIcon from '@mui/icons-material/FormatItalic';
import FormatUnderlinedIcon from '@mui/icons-material/FormatUnderlined';
import FormatListBulletedIcon from '@mui/icons-material/FormatListBulleted';
import FormatListNumberedIcon from '@mui/icons-material/FormatListNumbered';
import UndoIcon from '@mui/icons-material/Undo';
import RedoIcon from '@mui/icons-material/Redo';
import DataObjectIcon from '@mui/icons-material/DataObject';
import styles from "./RichTextEditor.module.css";

export interface RichTextToken {
    label: string;
    token: string;
}

interface RichTextEditorProps {
    value: string;
    onChange: (html: string) => void;
    tokens?: RichTextToken[];
    placeholder?: string;
    minHeight?: number;
}

const RichTextEditor = ({ value, onChange, tokens = [], placeholder, minHeight = 260 }: RichTextEditorProps) => {
    const editorRef = useRef<HTMLDivElement>(null);
    const savedRangeRef = useRef<Range | null>(null);
    const [tokenMenuOpen, setTokenMenuOpen] = useState(false);

    // Only sync external value into the DOM when it actually differs from what's
    // currently rendered, so we don't clobber the caret position on every keystroke.
    useEffect(() => {
        if (editorRef.current && editorRef.current.innerHTML !== (value || "")) {
            editorRef.current.innerHTML = value || "";
        }
    }, [value]);

    useEffect(() => {
        if (!tokenMenuOpen) return;
        const closeMenu = () => setTokenMenuOpen(false);
        document.addEventListener('click', closeMenu);
        return () => document.removeEventListener('click', closeMenu);
    }, [tokenMenuOpen]);

    function emitChange() {
        if (editorRef.current) {
            onChange(editorRef.current.innerHTML);
        }
    }

    function saveSelection() {
        const sel = window.getSelection();
        if (sel && sel.rangeCount > 0 && editorRef.current?.contains(sel.anchorNode)) {
            savedRangeRef.current = sel.getRangeAt(0).cloneRange();
        }
    }

    function focusEditor() {
        editorRef.current?.focus();
        const sel = window.getSelection();
        if (savedRangeRef.current && sel) {
            sel.removeAllRanges();
            sel.addRange(savedRangeRef.current);
        }
    }

    function runCommand(command: string, arg?: string) {
        focusEditor();
        document.execCommand(command, false, arg);
        emitChange();
    }

    function insertToken(token: string) {
        setTokenMenuOpen(false);
        focusEditor();
        document.execCommand('insertText', false, `{{${token}}}`);
        emitChange();
    }

    return (
        <div className={styles.editorWrap}>
            <div className={styles.toolbar}>
                <button type="button" className={styles.toolBtn} onMouseDown={(e) => e.preventDefault()} onClick={() => runCommand('bold')} title="Bold">
                    <FormatBoldIcon fontSize="small" />
                </button>
                <button type="button" className={styles.toolBtn} onMouseDown={(e) => e.preventDefault()} onClick={() => runCommand('italic')} title="Italic">
                    <FormatItalicIcon fontSize="small" />
                </button>
                <button type="button" className={styles.toolBtn} onMouseDown={(e) => e.preventDefault()} onClick={() => runCommand('underline')} title="Underline">
                    <FormatUnderlinedIcon fontSize="small" />
                </button>
                <span className={styles.toolDivider} />
                <button type="button" className={styles.toolBtn} onMouseDown={(e) => e.preventDefault()} onClick={() => runCommand('insertUnorderedList')} title="Bullet list">
                    <FormatListBulletedIcon fontSize="small" />
                </button>
                <button type="button" className={styles.toolBtn} onMouseDown={(e) => e.preventDefault()} onClick={() => runCommand('insertOrderedList')} title="Numbered list">
                    <FormatListNumberedIcon fontSize="small" />
                </button>
                <span className={styles.toolDivider} />
                <button type="button" className={styles.toolBtn} onMouseDown={(e) => e.preventDefault()} onClick={() => runCommand('undo')} title="Undo">
                    <UndoIcon fontSize="small" />
                </button>
                <button type="button" className={styles.toolBtn} onMouseDown={(e) => e.preventDefault()} onClick={() => runCommand('redo')} title="Redo">
                    <RedoIcon fontSize="small" />
                </button>

                <span className={styles.toolSpacer} />

                {tokens.length > 0 && (
                    <div className={styles.tokenMenuWrap} onClick={(e) => e.stopPropagation()}>
                        <button
                            type="button"
                            className={styles.tokenTrigger}
                            onMouseDown={(e) => { e.preventDefault(); saveSelection(); }}
                            onClick={() => setTokenMenuOpen((o) => !o)}
                        >
                            <DataObjectIcon fontSize="small" />
                            Insert Token
                        </button>
                        {tokenMenuOpen && (
                            <div className={styles.tokenMenu}>
                                {tokens.map((t) => (
                                    <span key={t.token} className={styles.tokenMenuItem} onClick={() => insertToken(t.token)}>
                                        <span className={styles.tokenMenuLabel}>{t.label}</span>
                                        <span className={styles.tokenMenuCode}>{`{{${t.token}}}`}</span>
                                    </span>
                                ))}
                            </div>
                        )}
                    </div>
                )}
            </div>

            <div
                ref={editorRef}
                className={styles.editorBody}
                style={{ minHeight }}
                contentEditable
                suppressContentEditableWarning
                data-placeholder={placeholder}
                onInput={emitChange}
                onBlur={() => { saveSelection(); emitChange(); }}
                onKeyUp={saveSelection}
                onMouseUp={saveSelection}
            />
        </div>
    );
};

export default RichTextEditor;
